import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Star, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultFormData = { student_name: '', course: '', rating: 5, comment: '' };

const StarRating = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        onClick={() => onChange(star)}
        className="focus:outline-none"
      >
        <Star className={`w-6 h-6 transition-colors ${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
      </button>
    ))}
  </div>
);

const AdminTestimonials = () => {
  const { token } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/testimonials`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial added!');
      setDialogOpen(false);
      setFormData(defaultFormData);
      fetchTestimonials();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add testimonial');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API_URL}/api/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted!');
      fetchTestimonials();
    } catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <AdminLayout title="Testimonials">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Manage student testimonials shown on the home page.</p>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setFormData(defaultFormData); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-testimonial-btn"><Plus className="w-4 h-4 mr-2" /> Add Testimonial</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Testimonial</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Student Name</Label>
                  <Input value={formData.student_name} onChange={(e) => setFormData({...formData, student_name: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Course</Label>
                  <Input value={formData.course} onChange={(e) => setFormData({...formData, course: e.target.value})} placeholder="JEE Advanced" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Rating</Label>
                <StarRating value={formData.rating} onChange={(r) => setFormData({...formData, rating: r})} />
              </div>
              <div className="space-y-2">
                <Label>Comment</Label>
                <Textarea value={formData.comment} onChange={(e) => setFormData({...formData, comment: e.target.value})} rows={4} placeholder="Student's experience..." required />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Saving...
                  </span>
                ) : 'Add Testimonial'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-28" /></Card>)}
        </div>
      ) : testimonials.length > 0 ? (
        <div className="space-y-4">
          {testimonials.map((t) => (
            <Card key={t.id} data-testid={`testimonial-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="font-semibold">{t.student_name}</p>
                      <span className="text-sm text-muted-foreground">— {t.course}</span>
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`w-4 h-4 ${s <= t.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/20'}`} />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground italic">"{t.comment}"</p>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => handleDelete(t.id)} className="text-destructive shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-semibold">No Testimonials Yet</h3>
            <p className="text-muted-foreground text-sm mt-1">Add your first student testimonial.</p>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminTestimonials;
